import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  DataUsage as DataUsageIcon,
  Sms as SmsIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useUserStore } from "../../store";
import { formatDate } from "../../utils";
import PlanSelector from "./PlanSelector";
import TrialBanner from "../shared/TrialBanner";

function OrganizationBillingTab({ t }) {
  const { currentOrganization } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [seatData, setSeatData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [meteredPrices, setMeteredPrices] = useState(null);
  const [smsBilledPerSegment, setSmsBilledPerSegment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);

  useEffect(() => {
    fetchData();

    // Check for payment status in URL
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      setPaymentMessage({ type: "success", text: t("Payment successful! Your subscription is now active.") });
      // Clean up URL
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
    } else if (paymentStatus === "failed") {
      setPaymentMessage({ type: "error", text: t("Payment failed. Please try again.") });
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subscriptionRes, usageRes, seatsRes, plansRes] = await Promise.all([
        axios.get("/api/payments/subscription/info/").catch(() => ({ data: null })),
        axios.get("/api/payments/usage/").catch(() => ({ data: null })),
        axios.get("/api/payments/seats/").catch(() => ({ data: null })),
        axios.get("/api/payments/plans/").catch(() => ({ data: { plans: [] } })),
      ]);
      setSubscriptionInfo(subscriptionRes.data);
      setUsageData(usageRes.data);
      setSeatData(seatsRes.data);
      setPlans(plansRes.data?.plans || []);
      setMeteredPrices(plansRes.data?.metered_prices || null);
      setSmsBilledPerSegment(Boolean(plansRes.data?.sms_billed_per_segment));
    } catch (err) {
      setError(t("Failed to load subscription data"));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fi-FI", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "trialing":
        return "info";
      case "past_due":
        return "warning";
      case "canceled":
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "trialing":
        return <CheckCircleIcon fontSize="small" />;
      case "past_due":
      case "canceled":
      case "cancelled":
        return <WarningIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const hasSubscription = subscriptionInfo?.has_subscription;
  const isTrial = subscriptionInfo?.is_trial;
  const trialDaysRemaining = subscriptionInfo?.trial_days_remaining || 0;
  const currentPlan = subscriptionInfo?.plan;
  const effectivePrice = currentPlan?.effective_price || null;

  return (
    <Box>
      {/* Payment success/failure message */}
      {paymentMessage && (
        <Alert
          severity={paymentMessage.type}
          onClose={() => setPaymentMessage(null)}
          sx={{ mb: 3 }}
        >
          {paymentMessage.text}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
          {t("Billing & Subscription")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Manage your subscription and payment methods")}
        </Typography>
      </Box>

      {/* Trial Banner */}
      {isTrial && (
        <TrialBanner
          trialDaysRemaining={trialDaysRemaining}
          onUpgradeClick={() => setShowPlanSelector(true)}
        />
      )}

      <Grid container spacing={3}>
        {/* Current Plan Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CreditCardIcon color="primary" />
                <Typography variant="h6">{t("Current Plan")}</Typography>
              </Box>

              {hasSubscription ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                    >
                      {currentPlan?.display_name || t("Standard")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {isTrial && (
                        <Chip label={t("Trial")} color="info" size="small" />
                      )}
                      <Chip
                        icon={getStatusIcon(subscriptionInfo.status)}
                        label={subscriptionInfo.status}
                        color={getStatusColor(subscriptionInfo.status)}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("Billing")}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {subscriptionInfo.billing_period === "yearly"
                          ? t("Yearly")
                          : t("Monthly")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {isTrial ? t("Trial ends") : t("Next billing")}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {isTrial
                          ? subscriptionInfo.trial_end
                            ? formatDate(subscriptionInfo.trial_end)
                            : "-"
                          : subscriptionInfo.stripe_current_period_end
                          ? formatDate(subscriptionInfo.stripe_current_period_end)
                          : "-"}
                      </Typography>
                    </Grid>
                    {currentPlan?.price_monthly > 0 && !isTrial && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          {t("Plan price")}
                        </Typography>
                        {/* effective_price folds in a negotiated price and any percentage
                            discount (invoice billing); for Stripe it equals the list price. */}
                        <Typography variant="body1" fontWeight={500}>
                          {effectivePrice
                            ? formatCurrency(effectivePrice.price)
                            : subscriptionInfo.billing_period === "yearly"
                            ? formatCurrency(currentPlan.price_yearly)
                            : formatCurrency(currentPlan.price_monthly)}
                          {" / "}
                          {subscriptionInfo.billing_period === "yearly"
                            ? t("year")
                            : t("month")}
                        </Typography>
                        {effectivePrice?.discount_active && (
                          <Typography variant="caption" color="text.secondary" display="block" data-testid="plan-price-discount">
                            {effectivePrice.discount_last_day
                              ? t("Includes a discount of {{percent}} % until {{date}}.", {
                                  percent: String(Number(effectivePrice.discount_percent)).replace(".", ","),
                                  date: formatDate(effectivePrice.discount_last_day),
                                })
                              : t("Includes a discount of {{percent}} %.", {
                                  percent: String(Number(effectivePrice.discount_percent)).replace(".", ","),
                                })}
                            {effectivePrice.discount_last_day
                              ? " " +
                                t("After that {{price}} / {{unit}}.", {
                                  price: formatCurrency(effectivePrice.price_after),
                                  unit: subscriptionInfo.billing_period === "yearly" ? t("year") : t("month"),
                                })
                              : ""}
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {t("No active subscription")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("Start your subscription to access all features")}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowPlanSelector(true)}
                  >
                    {t("Choose a Plan")}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Seat Usage Card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PeopleIcon color="primary" />
                <Typography variant="h6">{t("Seat Usage")}</Typography>
              </Box>

              {seatData ? (
                <>
                  {/* Users Section */}
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("Users")}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("Active users")} + {t("Pending invitations")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.is_unlimited
                          ? t("Unlimited")
                          : `${seatData.seats_total_used} / ${seatData.seat_limit}`}
                      </Typography>
                    </Box>
                    {!seatData.is_unlimited && (
                      <LinearProgress
                        variant="determinate"
                        value={seatData.usage_percent || 0}
                        color={seatData.usage_percent >= 90 ? "warning" : "primary"}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    )}
                  </Box>

                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        {t("Active")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.seats_occupied}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        {t("Pending")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.seats_pending}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        {t("Available")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.is_unlimited
                          ? t("Unlimited")
                          : seatData.seats_available}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Employees Section */}
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("Employees")}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("Active employees")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.is_unlimited
                          ? t("Unlimited")
                          : `${seatData.employees_count} / ${seatData.employees_limit}`}
                      </Typography>
                    </Box>
                    {!seatData.is_unlimited && (
                      <LinearProgress
                        variant="determinate"
                        value={seatData.employees_limit > 0 ? Math.round((seatData.employees_count / seatData.employees_limit) * 100) : 0}
                        color={seatData.employees_limit > 0 && (seatData.employees_count / seatData.employees_limit) >= 0.9 ? "warning" : "primary"}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    )}
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        {t("Active")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.employees_count}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        {t("Available")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.is_unlimited
                          ? t("Unlimited")
                          : seatData.employees_available}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">
                        {t("Limit")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {seatData.is_unlimited
                          ? t("Unlimited")
                          : seatData.employees_limit}
                      </Typography>
                    </Grid>
                  </Grid>

                  {(!seatData.can_add_seat || !seatData.can_add_employee) && !seatData.is_unlimited && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {t("You've reached your seat limit. Upgrade your plan to add more users or employees.")}
                    </Alert>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("Seat information not available")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Plan Selection */}
        {(showPlanSelector || !hasSubscription || isTrial) && plans.length > 0 && (
          <Grid item xs={12} id="plans-section">
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    cursor: hasSubscription && !isTrial ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (hasSubscription && !isTrial) {
                      setShowPlanSelector(!showPlanSelector);
                    }
                  }}
                >
                  <Typography variant="h6">
                    {isTrial ? t("Choose Your Plan") : t("Available Plans")}
                  </Typography>
                  {hasSubscription && !isTrial && (
                    showPlanSelector ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  )}
                </Box>

                <Collapse in={showPlanSelector || !hasSubscription || isTrial}>
                  <PlanSelector
                    plans={plans}
                    meteredPrices={meteredPrices}
                    smsBilledPerSegment={smsBilledPerSegment}
                    currentPlan={currentPlan}
                    isTrialActive={isTrial}
                    onCheckoutStarted={() => {}}
                  />
                </Collapse>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Billing Actions Card */}
        {hasSubscription && !isTrial && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ReceiptIcon color="primary" />
                  <Typography variant="h6">{t("Billing Actions")}</Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    href={`/api/payments/stripe-create-portal-session/?organization=${currentOrganization?.organization_id || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Manage Subscription")}
                  </Button>

                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => setShowPlanSelector(!showPlanSelector)}
                  >
                    {showPlanSelector ? t("Hide Plans") : t("Change Plan")}
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t(
                      "Access the billing portal to update payment methods, view invoices, or manage your subscription."
                    )}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Usage Card */}
        {hasSubscription && usageData && (
          <Grid item xs={12} md={hasSubscription && !isTrial ? 6 : 12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <DataUsageIcon color="primary" />
                  <Typography variant="h6">{t("Current Period Usage")}</Typography>
                </Box>

                {usageData.period_start && usageData.period_end && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t("Billing period")}: {formatDate(usageData.period_start)} -{" "}
                    {formatDate(usageData.period_end)}
                  </Typography>
                )}

                <Grid container spacing={3}>
                  {/* SMS Usage */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, backgroundColor: "background.default" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <SmsIcon color="action" fontSize="small" />
                        <Typography variant="subtitle2">{t("SMS Messages")}</Typography>
                      </Box>
                      <Typography variant="h4" fontWeight={600}>
                        {usageData.usage?.sms_sent || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("messages sent")}
                      </Typography>
                      {usageData.pricing?.sms_sent && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {formatCurrency(usageData.pricing.sms_sent.unit_price)} /{" "}
                          {t("message")}
                        </Typography>
                      )}
                      {usageData.estimated_charges && (
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ mt: 1 }}
                        >
                          {t("Estimated")}: {formatCurrency(usageData.estimated_charges.sms_sent)}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* E-Invoice Usage */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, backgroundColor: "background.default" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <DescriptionIcon color="action" fontSize="small" />
                        <Typography variant="subtitle2">{t("E-Invoices")}</Typography>
                      </Box>
                      <Typography variant="h4" fontWeight={600}>
                        {usageData.usage?.einvoice_sent || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("invoices sent")}
                      </Typography>
                      {usageData.pricing?.einvoice_sent && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {formatCurrency(usageData.pricing.einvoice_sent.unit_price)} /{" "}
                          {t("invoice")}
                        </Typography>
                      )}
                      {usageData.estimated_charges && (
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ mt: 1 }}
                        >
                          {t("Estimated")}: {formatCurrency(usageData.estimated_charges.einvoice_sent)}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* OEM service schedule lookups */}
                  {(currentOrganization?.oem_service_enabled || (usageData.usage?.oem_lookup || 0) > 0) && (
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, backgroundColor: "background.default" }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <BuildIcon color="action" fontSize="small" />
                          <Typography variant="subtitle2">{t("oem.usageTileTitle")}</Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={600}>
                          {usageData.usage?.oem_lookup || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("oem.usageTileUnit")}
                        </Typography>
                        {usageData.pricing?.oem_lookup && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {formatCurrency(usageData.pricing.oem_lookup.unit_price)} /{" "}
                            {t("oem.usageTilePerUnit")}
                          </Typography>
                        )}
                        {usageData.estimated_charges && (
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            sx={{ mt: 1 }}
                          >
                            {t("Estimated")}: {formatCurrency(usageData.estimated_charges.oem_lookup || 0)}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {/* Total Estimated Charges */}
                {usageData.estimated_charges && usageData.estimated_charges.total > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: "primary.50",
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600}>
                        {t("Estimated usage charges this period")}
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        {formatCurrency(usageData.estimated_charges.total)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {t("Usage charges will be added to your next invoice")}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Upgrade Recommendation */}
        {subscriptionInfo?.upgrade_recommendation && (
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="subtitle2" fontWeight={600}>
                {t("Upgrade Recommendation")}
              </Typography>
              <Typography variant="body2">
                {subscriptionInfo.upgrade_recommendation.reason}
              </Typography>
              {subscriptionInfo.upgrade_recommendation.recommended_plan && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => setShowPlanSelector(true)}
                >
                  {t("View {{plan}}", {
                    plan: subscriptionInfo.upgrade_recommendation.recommended_plan,
                  })}
                </Button>
              )}
            </Alert>
          </Grid>
        )}

        {/* Billing Info */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              {t("Need Help?")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("For billing questions or issues, please contact our support team at")}{" "}
              asiakaspalvelu@verstaani.fi
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default withTranslation()(OrganizationBillingTab);
